import { Box, Grid } from "@mui/material";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import {
  ArrayField,
  Button,
  Datagrid,
  FunctionField,
  SelectInput,
  TextField,
  TextInput,
  required,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";
import { settings } from "app";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import FormModal from "server/components/Modals/FormModal";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import dataProvider from "server/dataProvider";
import { doctoralRequests } from "api/doctoralApi";
import { DoctorantInterruptionTypeChoices, DoctoralSchoolPaymentProviderChoices } from "utils/choices";
import { DoctorantStatus } from "utils/constants";
import DoctoralDateField from "../../Partials/DoctoralDateField";

const interruptionTypeLabel = type =>
  DoctorantInterruptionTypeChoices.find(c => c.id === type)?.name ?? "—";

const InterruptDialog = ({ doctorantId }) => {
  const notify = useNotify();
  const refresh = useRefresh();

  const handleConfirm = async data => {
    await dataProvider.customRequest(
      doctoralRequests.interruptDoctorant(doctorantId),
      {
        type: data.type,
        orderNumber: data.orderNumber,
        orderDate: data.orderDate,
        effectiveFrom: data.effectiveFrom,
      },
      "POST",
    );
    notify("Докторантът е прекъснал успешно", { type: "success" });
    refresh();
  };

  return (
    <FormModal
      titleContent="Прекъсване на докторантура"
      saveTitle="Прекъсни"
      dialogSize="md"
      onConfirm={handleConfirm}
      triggerButton={
        <Button
          label="Прекъсване"
          variant="contained"
          startIcon={<PauseCircleIcon />}
        />
      }
    >
      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={6}>
          <SelectInput
            source="type"
            label="Тип прекъсване"
            choices={DoctorantInterruptionTypeChoices}
            validate={required("Задължително")}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomDateInput
            source="effectiveFrom"
            label="Считано от"
            validate={required("Задължително")}
            noTime
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput
            source="orderNumber"
            label="Номер на заповед"
            validate={required("Задължително")}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomDateInput
            source="orderDate"
            label="Дата на заповед"
            validate={required("Задължително")}
            noTime
            fullWidth
            required
          />
        </Grid>
      </Grid>
    </FormModal>
  );
};

const ReinstateDialog = ({ doctorantId }) => {
  const notify = useNotify();
  const refresh = useRefresh();

  const handleConfirm = async data => {
    await dataProvider.customRequest(
      doctoralRequests.reinstateDoctorant(doctorantId),
      { orderNumber: data.orderNumber, orderDate: data.orderDate, paymentProviderId: data.paymentProviderId },
      "POST",
    );
    notify("Докторантът е възстановен успешно", { type: "success" });
    refresh();
  };

  return (
    <FormModal
      titleContent="Възстановяване на докторантура"
      saveTitle="Възстанови"
      dialogSize="md"
      onConfirm={handleConfirm}
      triggerButton={
        <Button
          label="Възстановяване"
          variant="contained"
          startIcon={<PlayCircleIcon />}
        />
      }
    >
      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={6}>
          <TextInput
            source="orderNumber"
            label="Номер на заповед"
            validate={required("Задължително")}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomDateInput
            source="orderDate"
            label="Дата на заповед"
            validate={required("Задължително")}
            noTime
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectInput
            source="paymentProviderId"
            label="Начин на плащане на годишната такса"
            choices={DoctoralSchoolPaymentProviderChoices}
            validate={required("Задължително")}
            fullWidth
          />
        </Grid>
      </Grid>
    </FormModal>
  );
};

const InterruptionsSection = () => {
  const record = useRecordContext();

  if (!record?.id) return null;

  const isActive = record.status === DoctorantStatus.Active;
  const isInterrupted = record.status === DoctorantStatus.Interrupted;

  return (
    <Box>
      <SecuredComponent permission={permissions.canDismissDoctorants}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 1 }}>
          {isActive && <InterruptDialog doctorantId={record.id} />}
          {isInterrupted && <ReinstateDialog doctorantId={record.id} />}
        </Box>
      </SecuredComponent>

      <ArrayField source="interruptions">
        <Datagrid
          rowClick={false}
          bulkActionButtons={false}
          empty={<em>Няма прекъсвания</em>}
          sx={settings.style.secondaryDatagrid}
        >
          <FunctionField label="Тип" render={row => interruptionTypeLabel(row.type)} />
          <DoctoralDateField source="effectiveFrom" label="Считано от" />
          <TextField source="orderNumber" label="Заповед №" sortable={false} />
          <DoctoralDateField source="orderDate" label="Дата на заповед" />
          <TextField
            source="reinstateOrderNumber"
            label="Заповед № за възстановяване"
            emptyText="—"
            sortable={false}
          />
          <DoctoralDateField source="reinstateOrderDate" label="Дата на възстановяване" />
        </Datagrid>
      </ArrayField>
    </Box>
  );
};

export default InterruptionsSection;
